import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonItem,
  IonIcon,
  IonLabel,
  IonInput,
  IonButton,
  IonSpinner,
  IonText,
  IonToast,
  IonList,
  IonAlert,
} from '@ionic/react';
import { pulseOutline, radioOutline, personOutline, qrCodeOutline, lockClosedOutline } from 'ionicons/icons';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAdminHubDetail } from '../../../application/use-cases/admin/useAdminHubs';
import { RESET_REASON_BY_CODE, RESET_REASON_LABEL, formatUptime } from '../../../domain/DeviceHealth';
import { wifiQuality, type HubHealthPoint } from '../../../domain/Hub';
import { formatRelative } from '../../../domain/WeatherStation';
import { Tile } from '../../components/admin/DeviceHealthCard';
import './Admin.css';

const formatTick = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// WiFi del casco (dBm) y memoria libre (KB), por hora, últimos 7 días: un
// WiFi que se degrada de noche o una memoria que baja de a poco avisan antes
// de que el hub se caiga.
const HubHealthChart: React.FC<{ points: HubHealthPoint[] }> = ({ points }) => (
  <ResponsiveContainer width="100%" height={180}>
    <LineChart data={points} margin={{ top: 8, right: 4, bottom: 0, left: 4 }}>
      <CartesianGrid vertical={false} stroke="var(--domiot-border)" strokeDasharray="3 3" />
      <XAxis
        dataKey="at"
        tickFormatter={formatTick}
        minTickGap={32}
        tick={{ fontSize: 11, fill: 'var(--domiot-text-muted)' }}
        axisLine={{ stroke: 'var(--domiot-border)' }}
        tickLine={false}
      />
      <YAxis
        yAxisId="wifi"
        tick={{ fontSize: 11, fill: 'var(--domiot-text-muted)' }}
        axisLine={false}
        tickLine={false}
        width={44}
        domain={[-100, -30]}
        tickFormatter={(v: number) => `${v}`}
      />
      <YAxis yAxisId="heap" orientation="right" hide domain={[0, 'auto']} />
      <Tooltip
        labelFormatter={(iso) =>
          new Date(String(iso)).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
        }
        formatter={(v, name) => (name === 'wifiRssi' ? [`${v} dBm`, 'WiFi'] : [`${v} KB`, 'Memoria libre'])}
        contentStyle={{ background: 'var(--domiot-surface)', border: '1px solid var(--domiot-border)', borderRadius: 10, fontSize: 12 }}
      />
      <Line yAxisId="heap" type="monotone" dataKey="freeHeapKb" stroke="var(--domiot-warning)" strokeWidth={1.5} dot={false} />
      <Line yAxisId="wifi" type="monotone" dataKey="wifiRssi" stroke="var(--domiot-accent)" strokeWidth={2} dot={false} />
    </LineChart>
  </ResponsiveContainer>
);

const AdminHubDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { data, loading, error, toast, setToast, email, setEmail, busy, assign, issued, regenerateCode, setBrokerAccess } =
    useAdminHubDetail(id);
  const [confirmBlock, setConfirmBlock] = React.useState(false);
  const hub = data?.hub;
  const info = hub?.lastInfo ?? null;
  const reset = info?.resetReason !== undefined ? RESET_REASON_LABEL[RESET_REASON_BY_CODE[info.resetReason] ?? 'unknown'] : null;
  const wifi = wifiQuality(info?.wifiRssi ?? null);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/admin/hubs" />
          </IonButtons>
          <IonTitle>{id}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {loading && (
          <div className="ion-text-center">
            <IonSpinner color="primary" />
          </div>
        )}
        {error && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        {hub && data && (
          <>
            <IonCard className="admin-detail-card">
              <IonCardHeader>
                <IonCardTitle>
                  <span className="admin-card-icon tone-red">
                    <IonIcon icon={pulseOutline} />
                  </span>
                  Salud del hub
                </IonCardTitle>
                <IonCardSubtitle>
                  {hub.status === 'online' ? 'En línea' : 'Sin conexión'}
                  {hub.lastSeenAt && ` · último contacto ${formatRelative(hub.lastSeenAt)}`}
                  {info?.fw && ` · firmware ${info.fw}`}
                </IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                {!info ? (
                  <p className="admin-list-row-meta">Todavía no mandó su salud (WiFi, memoria, reinicios…).</p>
                ) : (
                  <div className="admin-health-grid">
                    {info.bateriaMv !== undefined && (
                      <Tile
                        label="Batería"
                        value={info.bateriaPct !== undefined ? `${info.bateriaPct}%` : `${(info.bateriaMv / 1000).toFixed(2)} V`}
                        detail={`${(info.bateriaMv / 1000).toFixed(2)} V${info.cargando ? ' · cargando' : ' · lo sostiene si se corta la luz'}`}
                        color={info.bateriaMv < 3500 ? 'danger' : undefined}
                      />
                    )}
                    {info.wifiRssi !== undefined && (
                      <Tile
                        label="WiFi del casco"
                        value={`${info.wifiRssi} dBm`}
                        detail={wifi ?? undefined}
                        color={wifi === 'débil' ? 'danger' : wifi === 'regular' ? 'warning' : undefined}
                      />
                    )}
                    {info.estaciones !== undefined && (
                      <Tile label="Estaciones" value={`${info.online ?? 0} de ${info.estaciones}`} detail="escuchándose ahora" />
                    )}
                    {info.freeHeapKb !== undefined && <Tile label="Memoria libre" value={`${info.freeHeapKb} KB`} />}
                    {info.uptimeS !== undefined && (
                      <Tile
                        label="Encendido hace"
                        value={formatUptime(info.uptimeS)}
                        detail={reset ? `último reinicio: ${reset.text}` : undefined}
                        color={reset?.bad ? 'warning' : undefined}
                      />
                    )}
                    <Tile
                      label="Reinicios"
                      value={String(data.restarts7d)}
                      color={data.restarts7d > 3 ? 'warning' : undefined}
                      detail={data.restarts7d > 3 ? '7 días · cortes de luz o se cuelga' : '7 días'}
                    />
                  </div>
                )}

                {data.hourly.length > 1 && (
                  <>
                    <p className="admin-list-row-meta admin-health-legend">
                      <span style={{ background: 'var(--domiot-accent)' }} />
                      WiFi (dBm)
                      <span style={{ background: 'var(--domiot-warning)' }} />
                      Memoria libre · últimos 7 días
                    </p>
                    <HubHealthChart points={data.hourly} />
                  </>
                )}

                {info && (
                  <>
                    <p className="admin-list-row-meta" style={{ marginTop: 12 }}>
                      Lo que llegó
                    </p>
                    <code className="admin-mqtt-payload">{JSON.stringify(info)}</code>
                  </>
                )}
              </IonCardContent>
            </IonCard>

            <IonCard className="admin-detail-card">
              <IonCardHeader>
                <IonCardTitle>
                  <span className="admin-card-icon tone-blue">
                    <IonIcon icon={radioOutline} />
                  </span>
                  Estaciones ({data.stations.length})
                </IonCardTitle>
                <IonCardSubtitle>
                  El hub informa {hub.stationIds.length} emparejadas
                  {hub.stationIds.length ? `: ${hub.stationIds.join(', ')}` : ''}
                </IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList>
                  {data.stations.map((s) => (
                    <IonItem key={s.id} button detail onClick={() => history.push(`/app/admin/devices/${s.id}/mqtt`)}>
                      <IonLabel>
                        <h3>{s.name}</h3>
                        <p>
                          {s.status === 'online' ? 'Online' : 'Sin conexión'}
                          {s.lastSeenAt && ` · ${formatRelative(s.lastSeenAt)}`}
                        </p>
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
                {data.stations.length === 0 && (
                  <p className="admin-list-row-meta">
                    {hub.userId ? 'Ninguna en la app todavía.' : 'Se crean en la cuenta del cliente cuando vincule el hub.'}
                  </p>
                )}
              </IonCardContent>
            </IonCard>

            <IonCard className="admin-detail-card">
              <IonCardHeader>
                <IonCardTitle>
                  <span className="admin-card-icon tone-green">
                    <IonIcon icon={personOutline} />
                  </span>
                  Dueño
                </IonCardTitle>
                <IonCardSubtitle>
                  {hub.ownerEmail ? `${hub.ownerEmail}${hub.siteName ? ` · ${hub.siteName}` : ''}` : 'Stock: todavía sin dueño'}
                </IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <IonItem>
                  <IonInput
                    label="Asignar a (email del cliente)"
                    labelPlacement="stacked"
                    type="email"
                    value={email}
                    onIonInput={(e) => setEmail(e.detail.value ?? '')}
                  />
                </IonItem>
                <IonButton expand="block" onClick={assign} disabled={busy || !email.trim()} style={{ marginTop: 12 }}>
                  Asignar hub
                </IonButton>
                <p className="admin-list-row-meta">
                  Para enviárselo ya vinculado. Sus estaciones aparecen solas en la cuenta del cliente.
                </p>

                <IonButton expand="block" fill="outline" onClick={regenerateCode} disabled={busy} style={{ marginTop: 16 }}>
                  <IonIcon icon={qrCodeOutline} slot="start" />
                  Nuevo código de vinculación
                </IonButton>
                {issued ? (
                  <>
                    <p className="admin-list-row-meta">
                      Código nuevo (se muestra una sola vez): <strong>{issued.claimCode}</strong>
                    </p>
                    <code className="admin-mqtt-payload">{issued.claimUrl}</code>
                  </>
                ) : (
                  <p className="admin-list-row-meta">
                    Si se perdió la etiqueta. El código anterior deja de valer. Para reimprimirla conviene pasar el hub por el
                    taller con --sin-grabar --nuevo-codigo, que además le da una contraseña MQTT nueva.
                  </p>
                )}
              </IonCardContent>
            </IonCard>
          </>
        )}

        {hub && (
          <IonCard className="admin-detail-card">
            <IonCardHeader>
              <IonCardTitle>
                <span className="admin-card-icon tone-red">
                  <IonIcon icon={lockClosedOutline} />
                </span>
                Acceso al broker
              </IonCardTitle>
              <IonCardSubtitle>Usuario MQTT {hub.id} · lo crea el backend cuando el hub pasa por el taller</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <p className="admin-list-row-meta">
                Si roban el hub o el cliente se da de baja: bloqueado, el broker lo desconecta y no lo deja volver a entrar.
              </p>
              <IonButton expand="block" color="danger" fill="outline" disabled={busy} onClick={() => setConfirmBlock(true)}>
                Bloquear en el broker
              </IonButton>
              <IonButton expand="block" fill="clear" disabled={busy} onClick={() => setBrokerAccess(true)}>
                Desbloquear
              </IonButton>
            </IonCardContent>
          </IonCard>
        )}

        <IonAlert
          isOpen={confirmBlock}
          header="¿Bloquear el hub?"
          message="Se desconecta del broker y dejan de llegar los datos de todas sus estaciones hasta que lo desbloquees."
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Bloquear', role: 'destructive', handler: () => setBrokerAccess(false) },
          ]}
          onDidDismiss={() => setConfirmBlock(false)}
        />

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={3000} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
};

export default AdminHubDetail;
